import React, { useState, useEffect, useMemo } from 'react';
import './PowerPlantError.css';
import { inverterErrorAPI, temperatureErrorAPI, panelErrorAPI } from '../../services/api';

const PowerPlantError = ({ powerPlant, configData }) => {
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedQueryType, setSelectedQueryType] = useState('today');
  // 오늘 날짜를 YYYY-MM-DD 형식으로 가져오기
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(getTodayDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [errorData, setErrorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 설정 데이터에서 인버터 정보 가져오기 (API에서 가져온 데이터 사용)
  const inverters = useMemo(() => {
    return configData?.inverterConfigs?.map(config => ({
      id: config.id.toString(), // inverterConfigId 사용
      inverterId: config.inverterId,
      name: config.name
    })) || [];
  }, [configData?.inverterConfigs]);


  // 설정 데이터에서 접속반 정보 가져오기 (패널 API 데이터를 접속반으로 사용)
  const connectionBoxes = useMemo(() => {
    return configData?.panelConfigs?.map(config => ({
      id: config.id.toString(),
      name: config.name,
      status: 'normal'
    })) || [];
  }, [configData?.panelConfigs]);

  // 설정 데이터에서 환경센서 정보 가져오기
  const environmentSensors = useMemo(() => {
    return configData?.temperatureConfig ? [{
      id: 'env-sensor-1',
      name: '환경센서'
    }] : [];
  }, [configData?.temperatureConfig]);

  // API에서 인버터 에러 데이터 조회
  const fetchInverterErrors = async (inverterConfigId, periodType = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        startDate: startDate,
        endDate: endDate,
        periodType: periodType
      };
      
      const response = await inverterErrorAPI.getInverterErrors(inverterConfigId, params);
      
      // API 응답을 UI에 맞게 변환
      const formattedErrors = response.map((error, index) => ({
        id: index + 1,
        occurrenceTime: error.createdAt ? error.createdAt.replace('T', ' ').substring(0, 19) : '-',
        errorCode: error.faultMessage || '에러 메시지 없음'
      }));
      
      setErrorData(formattedErrors);
    } catch (err) {
      console.error('Error fetching inverter errors:', err);
      setError('인버터 에러 데이터를 불러오는데 실패했습니다.');
      setErrorData([]);
    } finally {
      setLoading(false);
    }
  };

  // API에서 환경센서 에러 데이터 조회
  const fetchTemperatureErrors = async (periodType = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        startDate: startDate,
        endDate: endDate,
        periodType: periodType
      };
      
      const response = await temperatureErrorAPI.getTemperatureErrors(powerPlant.id, params);
      
      // API 응답을 UI에 맞게 변환
      const formattedErrors = response.map((error, index) => ({
        id: index + 1,
        occurrenceTime: error.createdAt ? error.createdAt.replace('T', ' ').substring(0, 19) : '-',
        errorCode: error.faultMessage || '에러 메시지 없음'
      }));
      
      setErrorData(formattedErrors);
    } catch (err) {
      console.error('Error fetching temperature errors:', err);
      setError('환경센서 에러 데이터를 불러오는데 실패했습니다.');
      setErrorData([]);
    } finally {
      setLoading(false);
    }
  };

  // API에서 접속반 에러 데이터 조회
  const fetchPanelErrors = async (panelConfigId, periodType = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        startDate: startDate,
        endDate: endDate,
        periodType: periodType
      };
      
      const response = await panelErrorAPI.getPanelErrors(panelConfigId, params);
      
      // API 응답을 UI에 맞게 변환
      const formattedErrors = response.map((error, index) => ({
        id: index + 1,
        occurrenceTime: error.createdAt ? error.createdAt.replace('T', ' ').substring(0, 19) : '-',
        errorCode: error.faultMessage || '에러 메시지 없음'
      }));
      
      setErrorData(formattedErrors);
    } catch (err) {
      console.error('Error fetching panel errors:', err);
      setError('접속반 에러 데이터를 불러오는데 실패했습니다.');
      setErrorData([]);
    } finally {
      setLoading(false);
    }
  };

  // 조회 버튼 클릭
  const handleQuery = async () => {
    if (!selectedEquipment) return;
    
    if (selectedEquipment.type === 'inverter') {
      // 인버터의 경우 실제 API 호출
      await fetchInverterErrors(selectedEquipment.id);
    } else if (selectedEquipment.type === 'environmentSensor') {
      // 환경센서의 경우 실제 API 호출
      await fetchTemperatureErrors();
    } else if (selectedEquipment.type === 'connectionBox') {
      // 접속반의 경우 실제 API 호출
      await fetchPanelErrors(selectedEquipment.id);
    }
  };

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!errorData) return;
    
    const headers = ['번호', '발생일시', '에러코드'];
    const csvContent = [
      headers.join(','),
      ...errorData.map(row => [
        row.id,
        row.occurrenceTime,
        `"${row.errorCode}"`
      ].join(','))
    ].join('\n');
    
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `error_${selectedEquipment?.type}_${startDate}_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 조회 기간 선택 버튼 클릭
  const handlePeriodSelect = async (period) => {
    setSelectedQueryType(period);
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    
    let newStartDate = todayStr;
    let newEndDate = todayStr;
    let periodType = null;
    
    switch (period) {
      case 'today':
        newStartDate = todayStr;
        newEndDate = todayStr;
        periodType = 'DAILY';
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        newStartDate = weekAgo.toISOString().slice(0, 10);
        newEndDate = todayStr;
        periodType = 'WEEKLY';
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        newStartDate = monthAgo.toISOString().slice(0, 10);
        newEndDate = todayStr;
        periodType = 'MONTHLY';
        break;
      case 'year':
        const yearAgo = new Date(today);
        yearAgo.setFullYear(today.getFullYear() - 1);
        newStartDate = yearAgo.toISOString().slice(0, 10);
        newEndDate = todayStr;
        periodType = 'YEARLY';
        break;
      default:
        newStartDate = todayStr;
        newEndDate = todayStr;
        periodType = 'DAILY';
        break;
    }
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    
    // 기간 선택 후 자동으로 조회 실행
    setTimeout(async () => {
      if (selectedEquipment) {
        if (selectedEquipment.type === 'inverter') {
          // 인버터: 상태 업데이트 후 API 호출을 위해 임시로 params 직접 전달
          setLoading(true);
          setError(null);
          try {
            const params = {
              startDate: newStartDate,
              endDate: newEndDate,
              periodType: periodType
            };
            
            const response = await inverterErrorAPI.getInverterErrors(selectedEquipment.id, params);
            
            const formattedErrors = response.map((error, index) => ({
              id: index + 1,
              occurrenceTime: error.createdAt ? error.createdAt.replace('T', ' ').substring(0, 19) : '-',
              errorCode: error.faultMessage || '에러 메시지 없음'
            }));
            
            setErrorData(formattedErrors);
          } catch (err) {
            console.error('Error fetching inverter errors:', err);
            setError('인버터 에러 데이터를 불러오는데 실패했습니다.');
            setErrorData([]);
          } finally {
            setLoading(false);
          }
        } else if (selectedEquipment.type === 'environmentSensor') {
          // 환경센서: 상태 업데이트 후 API 호출
          setLoading(true);
          setError(null);
          try {
            const params = {
              startDate: newStartDate,
              endDate: newEndDate,
              periodType: periodType
            };
            
            const response = await temperatureErrorAPI.getTemperatureErrors(powerPlant.id, params);
            
            const formattedErrors = response.map((error, index) => ({
              id: index + 1,
              occurrenceTime: error.createdAt ? error.createdAt.replace('T', ' ').substring(0, 19) : '-',
              errorCode: error.faultMessage || '에러 메시지 없음'
            }));
            
            setErrorData(formattedErrors);
          } catch (err) {
            console.error('Error fetching temperature errors:', err);
            setError('환경센서 에러 데이터를 불러오는데 실패했습니다.');
            setErrorData([]);
          } finally {
            setLoading(false);
          }
        } else if (selectedEquipment.type === 'connectionBox') {
          // 접속반: 상태 업데이트 후 API 호출
          setLoading(true);
          setError(null);
          try {
            const params = {
              startDate: newStartDate,
              endDate: newEndDate,
              periodType: periodType
            };
            
            const response = await panelErrorAPI.getPanelErrors(selectedEquipment.id, params);
            
            const formattedErrors = response.map((error, index) => ({
              id: index + 1,
              occurrenceTime: error.createdAt ? error.createdAt.replace('T', ' ').substring(0, 19) : '-',
              errorCode: error.faultMessage || '에러 메시지 없음'
            }));
            
            setErrorData(formattedErrors);
          } catch (err) {
            console.error('Error fetching panel errors:', err);
            setError('접속반 에러 데이터를 불러오는데 실패했습니다.');
            setErrorData([]);
          } finally {
            setLoading(false);
          }
        }
      }
    }, 100);
  };


  // 컴포넌트 마운트 시 첫 번째 인버터 자동 선택
  useEffect(() => {
    if (inverters.length > 0 && !selectedEquipment) {
      const firstInverter = { type: 'inverter', data: inverters[0], id: inverters[0].id };
      setSelectedEquipment(firstInverter);
      // selectedEquipment가 변경되면 아래 useEffect에서 자동으로 데이터 조회됨
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 장비 선택이 변경될 때마다 자동으로 데이터 조회
  useEffect(() => {
    if (selectedEquipment) {
      if (selectedEquipment.type === 'inverter') {
        // 인버터의 경우 실제 API 호출
        fetchInverterErrors(selectedEquipment.id);
      } else if (selectedEquipment.type === 'environmentSensor') {
        // 환경센서의 경우 실제 API 호출
        fetchTemperatureErrors();
      } else if (selectedEquipment.type === 'connectionBox') {
        // 접속반의 경우 실제 API 호출
        fetchPanelErrors(selectedEquipment.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEquipment]);

  // 드롭다운용 전체 장비 목록 생성
  const allEquipmentOptions = useMemo(() => {
    const options = [];
    // 인버터
    inverters.forEach(inverter => {
      options.push({
        value: `inverter-${inverter.id}`,
        label: `[인버터] ${inverter.name}`,
        type: 'inverter',
        data: inverter,
        id: inverter.id
      });
    });
    // 접속반
    connectionBoxes.forEach(box => {
      options.push({
        value: `connectionBox-${box.id}`,
        label: `[접속반] ${box.name}`,
        type: 'connectionBox',
        data: box,
        id: box.id
      });
    });
    // 환경센서
    environmentSensors.forEach(sensor => {
      options.push({
        value: `environmentSensor-${sensor.id}`,
        label: `[환경센서] ${sensor.name}`,
        type: 'environmentSensor',
        data: sensor,
        id: sensor.id
      });
    });
    return options;
  }, [inverters, connectionBoxes, environmentSensors]);

  // 드롭다운 선택 값
  const dropdownValue = useMemo(() => {
    if (!selectedEquipment || !selectedEquipment.type) return '';
    const id = selectedEquipment.id;
    return `${selectedEquipment.type}-${id}`;
  }, [selectedEquipment]);

  // 드롭다운 변경 핸들러
  const handleDropdownChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    
    const option = allEquipmentOptions.find(opt => opt.value === value);
    if (!option) return;

    setSelectedEquipment({ 
      type: option.type, 
      data: option.data,
      id: option.id 
    });
  };

  return (
    <div className="power-plant-error">
      {/* 모바일용 장비 선택 드롭다운 */}
      <div className="mobile-equipment-select">
        <select 
          value={dropdownValue}
          onChange={handleDropdownChange}
          className="equipment-dropdown"
        >
          <option value="">장비를 선택하세요</option>
          {allEquipmentOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="error-layout">
        {/* 왼쪽 패널 - 장비 목록 */}
        <div className="left-panel">
          {/* 인버터 섹션 */}
          <div className="equipment-section">
            <h3>인버터</h3>
            {inverters.map((inverter) => (
              <div 
                key={inverter.id} 
                className={`trend-equipment-item ${selectedEquipment?.type === 'inverter' && selectedEquipment?.id === inverter.id ? 'selected' : ''}`}
                onClick={() => setSelectedEquipment({ type: 'inverter', data: inverter, id: inverter.id })}
              >
                {inverter.name}
              </div>
            ))}
          </div>

          {/* 접속반 섹션 */}
          {connectionBoxes.length > 0 && (
          <div className="equipment-section">
            <h3>접속반</h3>
            {connectionBoxes.map((box) => (
              <div 
                key={box.id} 
                className={`trend-equipment-item ${selectedEquipment?.type === 'connectionBox' && selectedEquipment?.id === box.id ? 'selected' : ''}`}
                onClick={() => setSelectedEquipment({ type: 'connectionBox', data: box, id: box.id })}
              >
                {box.name}
              </div>
            ))}
          </div>
          )}

          {/* 환경센서 섹션 */}
          {environmentSensors.length > 0 && (
          <div className="equipment-section">
            <h3>환경센서</h3>
            {environmentSensors.map((sensor) => (
              <div 
                key={sensor.id} 
                className={`trend-equipment-item ${selectedEquipment?.type === 'environmentSensor' && selectedEquipment?.id === sensor.id ? 'selected' : ''}`}
                onClick={() => setSelectedEquipment({ type: 'environmentSensor', data: sensor, id: sensor.id })}
              >
                {sensor.name}
              </div>
            ))}
          </div>
          )}
        </div>

        {/* 중앙 패널 - 에러 내용 */}
        <div className="right-panel">
          {selectedEquipment ? (
            <>
              {/* 조회 조건 */}
              <div className="query-controls">
                <div className="query-left-section">
                  <div className="query-section">
                    <h4>조회기간 설정</h4>
                    <div className="date-inputs">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                      <span>-</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="query-section">
                    <h4>조회기간 선택</h4>
                    <div className="period-buttons">
                      <button 
                        className={selectedQueryType === 'today' ? 'active' : ''}
                        onClick={() => handlePeriodSelect('today')}
                      >
                        당일
                      </button>
                      <button 
                        className={selectedQueryType === 'week' ? 'active' : ''}
                        onClick={() => handlePeriodSelect('week')}
                      >
                        1주일
                      </button>
                      <button 
                        className={selectedQueryType === 'month' ? 'active' : ''}
                        onClick={() => handlePeriodSelect('month')}
                      >
                        1개월
                      </button>
                      <button 
                        className={selectedQueryType === 'year' ? 'active' : ''}
                        onClick={() => handlePeriodSelect('year')}
                      >
                        1년
                      </button>
                    </div>
                  </div>

                  <button className="query-button" onClick={handleQuery}>
                    조회
                  </button>
                </div>

                <button className="excel-button" onClick={handleExcelDownload}>
                  📊 엑셀다운로드
                </button>
              </div>

              {/* 에러 테이블 */}
              <div className="error-table-container">
                <h4>
                  {selectedEquipment.type === 'inverter' ? `인버터 > ${selectedEquipment.data.name}` :
                   selectedEquipment.type === 'connectionBox' ? `접속반 > ${selectedEquipment.data.name}` :
                   `환경센서 > ${selectedEquipment.data.name}`}
                </h4>
                
                {error && (
                  <div style={{ color: '#ef4444', marginBottom: '16px', padding: '12px', background: '#fee2e2', borderRadius: '6px' }}>
                    {error}
                  </div>
                )}
                
                <table className="error-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>발생일시</th>
                      <th>에러코드</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          데이터를 불러오는 중...
                        </td>
                      </tr>
                    ) : errorData && errorData.length > 0 ? (
                      errorData.map((error) => (
                        <tr key={error.id}>
                          <td>{error.id}</td>
                          <td>{error.occurrenceTime}</td>
                          <td>{error.errorCode}</td>
                        </tr>
                      ))
                    ) : errorData && errorData.length === 0 ? (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          에러 데이터가 없습니다
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          조회 버튼을 클릭하여 에러 데이터를 불러오세요
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>왼쪽에서 장비를 선택해주세요</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PowerPlantError;
